import React, { useState } from "react";
import { cn } from "../../lib/utils";

const PRICING_BREAKPOINTS = [
  { maxSqft: 1500, price: 399 },
  { maxSqft: 2999, price: 449 },
  { maxSqft: 4500, price: 549 },
];

const BREAKPOINT_SQFT_VALUES = [
  500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000, 7000, 8000, 9000, 10000,
];

const getPriceForSqft = (sqft: number): { price: number | null; label: string } => {
  if (sqft > 4500) return { price: null, label: "4500+ Sq Ft" };

  const breakpoint = PRICING_BREAKPOINTS.find((tier) => sqft <= tier.maxSqft);
  if (breakpoint) return { price: breakpoint.price, label: `${sqft.toLocaleString()} Sq Ft` };

  // This should not happen with our current breakpoints, but fallback to custom quote
  return { price: null, label: "Custom Quote" };
};

export const HudsonPricingSlider: React.FC = () => {
  const [sliderIndex, setSliderIndex] = useState(2); // Start at 1500 sq ft

  const sqft = BREAKPOINT_SQFT_VALUES[sliderIndex];
  const { price, label } = getPriceForSqft(sqft);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderIndex(Number(e.target.value));
  };

  const accentColor = '#9B775D';
  const bgCream = 'rgb(246, 241, 237)';
  const heading = 'rgb(15,15,15)';
  const sub = 'rgb(76,76,75)';

  return (
    <section className="max-w-4xl mx-auto p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Card */}
        <div className="flex-1 rounded-xl border border-gray-200 p-8 relative bg-white shadow-sm">
          <h2 className="text-sm font-semibold mb-4" style={{ color: heading }}>Calculate your pricing</h2>
          <div className="text-3xl font-bold mb-8" style={{ color: heading }}>{label}</div>
          <input
            type="range"
            min={0}
            max={BREAKPOINT_SQFT_VALUES.length - 1}
            step={1}
            value={sliderIndex}
            onChange={handleSliderChange}
            className="w-full appearance-none h-3 rounded bg-gray-200 mb-12"
            style={{
              background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${
                (sliderIndex / (BREAKPOINT_SQFT_VALUES.length - 1)) * 100
              }%, #E5E7EB ${(sliderIndex / (BREAKPOINT_SQFT_VALUES.length - 1)) * 100}%, #E5E7EB 100%)`,
            }}
          />

          <style>{`
            input[type='range']::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 28px;
              height: 28px;
              background: #ffffff;
              border: 2px solid ${accentColor};
              border-radius: 50%;
              cursor: pointer;
              margin-top: -1px;
              box-shadow: 0 1px 5px rgba(192, 192, 192, 0.5);
              position: relative;
            }
            input[type='range']::-moz-range-thumb {
              width: 26px;
              height: 26px;
              background: #ffffff;
              border: 2px solid ${accentColor};
              border-radius: 50%;
              cursor: pointer;
              box-shadow: 0 1px 5px rgba(192, 192, 192, 0.5);
            }
          `}</style>

          <div className="absolute bottom-8 left-8 right-8 flex items-center justify-between text-sm" style={{ color: sub }}>
            <span>Need a custom solution?</span>
            <a
              href="mailto:info@hudsonmyhome.com?subject=Custom%20Estate%20Pricing"
              className="font-medium flex items-center"
              style={{ color: accentColor }}
            >
              Contact us <span className="ml-1">→</span>
            </a>
          </div>
        </div>

        {/* Right Card */}
        <div className="flex-1 rounded-xl border border-gray-200 p-8 shadow-sm" style={{ backgroundColor: bgCream }}>
          <h2 className="text-sm font-semibold mb-4" style={{ color: heading }}>Your plan</h2>
          <div className="mb-2">
            <h3 className="text-3xl font-bold mb-6" style={{ color: heading }}>
              {price === null ? "Custom Quote" : `$${price} / mo`}
            </h3>
          </div>
          <p className="text-sm leading-relaxed mt-8" style={{ color: sub }}>
            {price === null
              ? "For estates over 4,500 sq ft, we provide custom pricing tailored to your property's unique needs and complexity."
              : `Premier Estate with Concierge service includes monthly inspections, proactive maintenance, vendor management, seasonal care, dedicated Home Director, annual assessment, white-glove service, full vendor coordination, maintenance scheduling, plus 12 hours of "fix-it list" work annually.`}
          </p>
          <a
            href="/membership"
            className="mt-8 inline-block text-white px-6 py-3 rounded-md font-semibold text-sm transition-colors hover:opacity-90"
            style={{ backgroundColor: accentColor }}
          >
            {price === null ? "Contact us" : "Discover Home Membership"}
          </a>
        </div>
      </div>
    </section>
  );
};
